import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import {
  FileText,
  ClipboardCheck,
  Send,
  XCircle,
  DollarSign,
  UserCheck,
  BarChart3,
  RefreshCcw,
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: FileText,
      title: 'Medical Coding',
      description: 'CPC-certified coders ensuring accurate ICD-10, CPT, and HCPCS coding for maximum reimbursement.',
    },
    {
      icon: ClipboardCheck,
      title: 'Eligibility & Benefits Verification',
      description: 'Real-time insurance verification to prevent claim denials and reduce AR days.',
    },
    {
      icon: Send,
      title: 'Claims Submission & Scrubbing',
      description: 'Automated claim scrubbing and submission with 98% clean claim rate.',
    },
    {
      icon: XCircle,
      title: 'Denial Management & Appeals',
      description: 'Expert appeal handling to recover denied claims and prevent future rejections.',
    },
    {
      icon: DollarSign,
      title: 'Patient Billing & Collections',
      description: 'Professional patient communication and payment collection services.',
    },
    {
      icon: UserCheck,
      title: 'Credentialing & Enrollment',
      description: 'Streamlined provider credentialing with insurance networks and payers.',
    },
    {
      icon: RefreshCcw,
      title: 'A/R Recovery',
      description: 'Aggressive accounts receivable follow-up to maximize cash flow.',
    },
    {
      icon: BarChart3,
      title: 'Revenue Cycle Analytics',
      description: 'Real-time dashboards and reporting for complete financial transparency.',
    },
  ];

  return (
    <section id="services" className="py-32 bg-gradient-to-br from-slate-50 to-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="text-[#14b8a6] font-semibold text-sm tracking-wider uppercase mb-4">
            Comprehensive Services
          </div>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            Complete Revenue Cycle Management
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            From coding to collections, we handle every aspect of your medical billing
            so you can focus on what matters most—patient care.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                whileHover={{ y: -8 }}
              >
                <Card className="h-full border-2 border-slate-100 hover:border-[#14b8a6]/30 transition-all duration-300 hover:shadow-xl hover:shadow-[#14b8a6]/5 group">
                  <CardContent className="p-8">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                      className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1e3a8a]/10 to-[#14b8a6]/10 flex items-center justify-center mb-6 group-hover:from-[#1e3a8a]/20 group-hover:to-[#14b8a6]/20 transition-all duration-300"
                    >
                      <Icon size={28} className="text-[#1e3a8a]" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
